/**
 * Echo Hunt -- a small numeric settings input (shared by the Settings-tab panels).
 *
 * Writes on blur/Enter rather than on every keystroke: a partially typed number is not a value, and
 * writing "0.4" through as "0" then "0." then "0.4" would briefly mean three different bands (or three
 * different thresholds). Escape abandons the edit rather than committing a half-typed value.
 *
 * Extracted from `JudgePanel` in P11, where `SuspicionPanel` needed the identical field -- two copies of a
 * commit-on-blur rule would drift, and the whole point of the rule is that it behaves the same everywhere.
 */
import React, {useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {TextInput} from "react-vcomponents";

export const NumberField = observer_mgl((props: {value: number, step?: number, min: number, max: number, onChange: (value: number)=>void, width?: number})=>{
	const {value, min, max, onChange} = props;
	const [draft, setDraft] = useState<string|null>(null);

	const commit = ()=>{
		if (draft == null) return;
		const parsed = Number(draft);
		setDraft(null);
		if (!Number.isFinite(parsed)) return;
		// `step` is advisory only: the fields are floats with a display precision, and rounding to the step
		// would silently reject a typed 0.62 because it is not a multiple of 0.05
		const clamped = Math.max(min, Math.min(max, parsed));
		if (clamped != value) onChange(clamped);
	};

	return (
		<TextInput style={{width: props.width ?? 70}} value={draft ?? String(value)} placeholder={String(value)}
			onChange={setDraft}
			onBlur={commit}
			onKeyDown={(e: React.KeyboardEvent)=>{
				if (e.key == "Enter") commit();
				if (e.key == "Escape") setDraft(null);
			}}/>
	);
});
